import React from "react";

interface PaginationMeta {
  total: number;
  perPage: number;
  currentPage: number;
  lastPage: number;
  firstPage: number;
  firstPageUrl?: string;
  lastPageUrl?: string;
  nextPageUrl?: string | null;
  previousPageUrl?: string | null;
}

interface PaginationProps {
  meta: PaginationMeta;
  onPageChange: (page: number) => void;
  className?: string;
}

const Pagination: React.FC<PaginationProps> = ({
  meta,
  onPageChange,
  className,
}) => {
  const total = Number(meta.total) || 0;
  const perPage = Number(meta.perPage) || 20;
  const currentPage = Number(meta.currentPage) || 1;
  const lastPage = Number(meta.lastPage) || 1;
  const firstPage = Number(meta.firstPage) || 1;

  const startItem = total > 0 ? (currentPage - 1) * perPage + 1 : 0;
  const endItem = Math.min(currentPage * perPage, total);

  if (lastPage <= 1) {
    return (
      <div className={`flex items-center justify-center ${className || ""}`}>
        <div className="text-xs text-gray-500">
          Menampilkan{" "}
          <span className="font-bold text-gray-700">{startItem}</span> -{" "}
          <span className="font-bold text-gray-700">{endItem}</span> dari{" "}
          <span className="font-bold text-gray-700">{total}</span> data
        </div>
      </div>
    );
  }

  const canGoPrev = currentPage > firstPage;
  const canGoNext = currentPage < lastPage;

  const handlePageClick = (page: number) => {
    if (page !== currentPage && page >= firstPage && page <= lastPage) {
      onPageChange(page);
    }
  };

  const renderPageNumbers = () => {
    const pages: React.ReactNode[] = [];
    let start = Math.max(firstPage, currentPage - 2);
    let end = Math.min(lastPage, currentPage + 2);

    if (currentPage <= 3) {
      end = Math.min(lastPage, 5);
    }
    if (currentPage >= lastPage - 2) {
      start = Math.max(firstPage, lastPage - 4);
    }

    if (start > firstPage) {
      pages.push(
        <button
          key={firstPage}
          onClick={() => handlePageClick(firstPage)}
          className="px-3 py-1.5 rounded-lg mx-0.5 font-bold text-xs border transition-all bg-white text-gray-600 border-gray-200 hover:bg-indigo-50"
        >
          {firstPage}
        </button>
      );
      if (start > firstPage + 1) {
        pages.push(
          <span key="start-ellipsis" className="px-2 text-gray-400">
            ...
          </span>
        );
      }
    }

    for (let i = start; i <= end; i++) {
      const isActive = i === currentPage;
      pages.push(
        <button
          key={i}
          onClick={() => handlePageClick(i)}
          className={`px-3 py-1.5 rounded-lg mx-0.5 font-bold text-xs border transition-all ${
            isActive
              ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
              : "bg-white text-gray-600 border-gray-200 hover:bg-indigo-50"
          }`}
        >
          {i}
        </button>
      );
    }

    if (end < lastPage) {
      if (end < lastPage - 1) {
        pages.push(
          <span key="end-ellipsis" className="px-2 text-gray-400">
            ...
          </span>
        );
      }
      pages.push(
        <button
          key={lastPage}
          onClick={() => handlePageClick(lastPage)}
          className="px-3 py-1.5 rounded-lg mx-0.5 font-bold text-xs border transition-all bg-white text-gray-600 border-gray-200 hover:bg-indigo-50"
        >
          {lastPage}
        </button>
      );
    }

    return pages;
  };

  return (
    <div
      className={`flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 ${
        className || ""
      }`}
    >
      <div className="text-xs text-gray-500">
        Menampilkan <span className="font-bold text-gray-700">{startItem}</span>{" "}
        - <span className="font-bold text-gray-700">{endItem}</span> dari{" "}
        <span className="font-bold text-gray-700">{total}</span> data
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => canGoPrev && handlePageClick(currentPage - 1)}
          disabled={!canGoPrev}
          className="px-3 py-1.5 rounded-lg font-bold text-xs border transition-all bg-white text-gray-600 border-gray-200 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
        >
          Prev
        </button>
        {renderPageNumbers()}
        <button
          onClick={() => canGoNext && handlePageClick(currentPage + 1)}
          disabled={!canGoNext}
          className="px-3 py-1.5 rounded-lg font-bold text-xs border transition-all bg-white text-gray-600 border-gray-200 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Pagination;
